import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLinkedin, FiMail, FiAward, FiBookOpen, FiTrendingUp } = FiIcons;

const Team = () => {
  const teamMembers = [
    {
      name: 'Ndleleni Maqubela',
      position: 'Board-Ready Executive',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
      bio: 'Highly accomplished executive with over two decades of experience in financial services and agricultural finance, with expertise in strategic leadership, operational transformation, and governance.',
      specializations: ['Governance Advisory', 'Strategic Financial Oversight', 'Leadership Development'],
      achievements: ['Board Readiness Programme', 'Non-Executive Director Programme', 'Management Development Programme']
    }
    // Structure maintained for future team members
  ];

  const expertiseAreas = [
    {
      icon: FiBookOpen,
      title: 'Skills Development',
      description: 'Comprehensive training programmes and learnership management',
      count: '500+'
    },
    {
      icon: FiAward,
      title: 'Governance Advisory',
      description: 'Regulatory compliance and policy implementation expertise',
      count: '50+'
    },
    {
      icon: FiTrendingUp,
      title: 'Business Training',
      description: 'Financial literacy and management development',
      count: '300+'
    }
  ];

  return (
    <section id="team" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Meet Our <span className="text-green-700">Leadership</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our leadership brings decades of experience in financial services, agricultural finance, 
            governance advisory, and business development to transform your organization.
          </p>
        </motion.div>

        {/* Expertise Stats */}
        <motion.div
          className="grid sm:grid-cols-3 gap-8 mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {expertiseAreas.map((area, index) => (
            <motion.div
              key={index}
              className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={area.icon} className="w-8 h-8 text-green-700" />
              </div>
              <div className="text-3xl font-bold text-green-700 mb-2">
                {area.count}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {area.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {area.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Team Members - Centered Layout for Single Member */}
        <div className="flex justify-center">
          <motion.div
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 max-w-md"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            {/* Image */}
            <div className="relative h-64 overflow-hidden">
              <img 
                src={teamMembers[0].image} 
                alt={teamMembers[0].name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              
              {/* Social Links */}
              <div className="absolute bottom-4 right-4 flex space-x-2">
                <a 
                  href="https://linkedin.com/in/ndleleni-maqubela-808920154/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white/20 backdrop-blur-sm p-2 rounded-full text-white hover:bg-white/30 transition-colors duration-200"
                >
                  <SafeIcon icon={FiLinkedin} className="w-4 h-4" />
                </a>
                <a 
                  href="mailto:info@nqobi.co.za" 
                  className="bg-white/20 backdrop-blur-sm p-2 rounded-full text-white hover:bg-white/30 transition-colors duration-200"
                >
                  <SafeIcon icon={FiMail} className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {teamMembers[0].name}
              </h3>
              <p className="text-green-700 font-medium mb-3">
                {teamMembers[0].position}
              </p>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                {teamMembers[0].bio}
              </p>

              {/* Specializations */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Specializations:
                </h4>
                <div className="flex flex-wrap gap-1">
                  {teamMembers[0].specializations.map((spec, specIndex) => (
                    <span
                      key={specIndex}
                      className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Key Achievements:
                </h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  {teamMembers[0].achievements.map((achievement, achIndex) => (
                    <li key={achIndex} className="flex items-center space-x-2">
                      <SafeIcon icon={FiAward} className="w-3 h-3 text-green-600 flex-shrink-0" />
                      <span>{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Languages */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Languages:
                </h4>
                <p className="text-xs text-gray-600">
                  siSwati, English, isiZulu, isiXhosa, Setswana, Sepedi, Sesotho
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16 bg-white p-12 rounded-2xl shadow-lg"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Work with Our Expert?
          </h3>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            With over two decades of experience in financial services and agricultural finance, 
            Ndleleni is ready to help transform your business through strategic guidance and governance expertise.
          </p>
          <motion.button
            className="bg-green-700 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-800 transition-colors duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Schedule a Consultation
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Team;